import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table, Toast } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'

import { createAdmin, getAllAdmins, updateAdmin } from '../../../../controller/api/adminAdmins'
import { PERMISSION_MODULES, formatPermissionText, normalizePermission } from '../../../../helper/permission'

const CreateOrEditAdminModal = ({ show, onHide, onSuccess, mode, admin }) => {
  const [cookies] = useCookies(['lottmekong'])
  const [form, setForm] = useState({ email: '', name: '', permission: '', password: '' })
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const normalizedPermission = useMemo(() => normalizePermission(form.permission || ''), [form.permission])

  const setPermissionAtIndex = useCallback(
    (index, enabled) => {
      const updated = normalizedPermission
        .split('')
        .map((char, currentIndex) => {
          if (currentIndex !== index) return char
          return enabled ? '1' : '0'
        })
        .join('')

      setForm((prev) => ({
        ...prev,
        permission: updated
      }))
    },
    [normalizedPermission]
  )

  useEffect(() => {
    if (!show) return

    if (mode === 'edit' && admin) {
      setForm({
        email: admin.email || '',
        name: admin.name || '',
        permission: admin.permission || '',
        password: ''
      })
    } else {
      setForm({ email: '', name: '', permission: '', password: '' })
    }

    setError('')
    setIsSubmitting(false)
  }, [admin, mode, show])

  const updateField = (field) => (evt) => {
    setForm((prev) => ({
      ...prev,
      [field]: evt.target.value
    }))
  }

  const onSubmit = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setError('')

    try {
      if (mode === 'create') {
        const email = typeof form.email === 'string' ? form.email.trim() : ''
        const password = typeof form.password === 'string' ? form.password.trim() : ''

        if (!email || !password) {
          setError('Vui lòng nhập email và mật khẩu')
          setIsSubmitting(false)
          return
        }

        if (password.length < 6) {
          setError('Mật khẩu phải có tối thiểu 6 ký tự')
          setIsSubmitting(false)
          return
        }

        const permissionValue = typeof form.permission === 'string' ? normalizePermission(form.permission.trim()) : ''

        const payload = {
          email,
          password,
          name: typeof form.name === 'string' && form.name.trim().length > 0 ? form.name.trim() : null,
          permission: permissionValue.includes('1') ? permissionValue : null
        }

        const resp = await createAdmin(cookies.lottmekong, payload)
        if (resp && resp.status === 'success') {
          onSuccess && onSuccess()
          onHide()
          return
        }

        setError(resp?.message || 'Có lỗi xảy ra')
        return
      }

      if (!admin?.id) {
        setError('Admin không hợp lệ')
        return
      }

      const permissionValue = typeof form.permission === 'string' ? normalizePermission(form.permission.trim()) : ''

      const payload = {
        id: admin.id,
        name: typeof form.name === 'string' && form.name.trim().length > 0 ? form.name.trim() : null,
        permission: permissionValue.includes('1') ? permissionValue : null
      }

      if (typeof form.password === 'string') {
        const trimmed = form.password.trim()
        if (trimmed.length > 0) {
          if (trimmed.length < 6) {
            setError('Mật khẩu mới phải có tối thiểu 6 ký tự')
            setIsSubmitting(false)
            return
          }
          payload.password = trimmed
        }
      }

      const resp = await updateAdmin(cookies.lottmekong, payload)
      if (resp && resp.status === 'success') {
        onSuccess && onSuccess()
        onHide()
        return
      }

      setError(resp?.message || 'Có lỗi xảy ra')
    } catch (err) {
      setError(err?.message || 'Có lỗi xảy ra')
    } finally {
      setIsSubmitting(false)
    }
  }

  const title = mode === 'create' ? 'Tạo nhân viên (Admin)' : 'Chỉnh sửa nhân viên (Admin)'

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Toast className="d-inline-block mb-3" onClose={() => setError('')} show={!!error} delay={3000} autohide bg="danger">
          <Toast.Header>
            <strong className="me-auto">Lỗi</strong>
          </Toast.Header>
          <Toast.Body>{error || 'Không thể thao tác'}</Toast.Body>
        </Toast>

        <Form>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="example@company.com"
                  value={form.email}
                  onChange={updateField('email')}
                  disabled={mode === 'edit'}
                  readOnly={mode === 'edit'}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Tên hiển thị</Form.Label>
                <Form.Control type="text" placeholder="Nhập tên" value={form.name} onChange={updateField('name')} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Permission</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {PERMISSION_MODULES.map((module) => (
                    <Form.Check
                      key={module.key}
                      type="checkbox"
                      id={`admin-permission-${module.key}`}
                      label={module.label}
                      checked={normalizedPermission[module.index] === '1'}
                      onChange={(e) => setPermissionAtIndex(module.index, e.target.checked)}
                    />
                  ))}

                  <div>
                    <Badge bg="light" text="dark" className="me-2">
                      {normalizedPermission}
                    </Badge>
                    <Badge bg="primary">{formatPermissionText(normalizedPermission)}</Badge>
                  </div>
                </div>

                <Form.Text className="text-muted">
                  Hệ thống lưu permission dạng chuỗi bit (vd: <code>00100</code>). Form này map sang checkbox để dễ chọn.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>{mode === 'create' ? 'Mật khẩu' : 'Mật khẩu mới'}</Form.Label>
                <Form.Control
                  type="password"
                  placeholder={mode === 'create' ? 'Tối thiểu 6 ký tự' : 'Để trống nếu không đổi'}
                  value={form.password}
                  onChange={updateField('password')}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Đóng
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const PermissionBadge = ({ permission }) => {
  const raw = `${permission ?? ''}`.replace(/\s+/g, '')
  if (!raw) return <Badge bg="secondary">(trống)</Badge>

  const value = normalizePermission(raw)
  const label = formatPermissionText(value)
  return (
    <>
      <Badge bg="primary">{label}</Badge>
    </>
  )
}

export default function AdminTable() {
  const [cookies] = useCookies(['lottmekong'])
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({ total: 0, data: [] })
  const [search, setSearch] = useState('')

  const [showCreate, setShowCreate] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await getAllAdmins(cookies.lottmekong, {
        page: 0,
        itemPerPage: 200,
        search
      })

      if (resp && resp.status === 'success') {
        setData({
          total: resp.total || 0,
          data: resp.data || []
        })
      } else {
        setData({ total: 0, data: [] })
      }
    } catch (e) {
      setData({ total: 0, data: [] })
    } finally {
      setLoading(false)
    }
  }, [cookies.lottmekong, search])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const rows = useMemo(() => (Array.isArray(data.data) ? data.data : []), [data.data])

  return (
    <>
      <Card border="light" className="shadow-sm">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5>Nhân viên (Admin) ({data.total})</h5>
            </Col>
            <Col className="text-end">
              <Button variant="success" type="button" onClick={() => setShowCreate(true)}>
                ➕ Tạo admin
              </Button>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Tìm theo email hoặc tên..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={6} className="text-end">
              <Button variant="outline-primary" onClick={fetchData} disabled={loading}>
                🔄 Làm mới
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Table responsive className="align-items-center table-flush">
          <thead className="thead-light">
            <tr>
              <th scope="col">Thao tác</th>
              <th scope="col">Email</th>
              <th scope="col">Tên</th>
              <th scope="col">Permission</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  <Spinner animation="border" className="me-2" />
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((it) => (
                <tr key={it.id}>
                  <td style={{ width: 140 }}>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        setSelectedAdmin(it)
                        setShowEdit(true)
                      }}
                    >
                      Chỉnh sửa
                    </Button>
                  </td>
                  <td>
                    <div className="fw-bold">{it.email}</div>
                  </td>
                  <td>{it.name || <span className="text-muted">(trống)</span>}</td>
                  <td>
                    <PermissionBadge permission={it.permission} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4 text-muted">
                  Không có admin nào
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <CreateOrEditAdminModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        onSuccess={fetchData}
        mode="create"
        admin={null}
      />

      <CreateOrEditAdminModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        onSuccess={fetchData}
        mode="edit"
        admin={selectedAdmin}
      />
    </>
  )
}
